import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useCurrentUserDetails } from 'entities/user/model';
import { useCallback } from 'react';

export const useSourceCapabilities = () => {
  const { hasAnyOrganization } = useCurrentUserDetails();
  const { getAccessTokenSilently } = useAuth0();
  const { data, isPending } = useQuery({
    queryKey: ['transfer-source-capabilities'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/public/asset-transfers/source-capabilities`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: hasAnyOrganization,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

  const _getDefaultSource = useCallback((): {
    currencySymbol: string;
    currency: string;
    network: string;
  } | null => {
    if (data && data.result?.capabilities?.currenciesByNetworks) {
      const networks = Object.keys(
        data.result.capabilities.currenciesByNetworks
      );
      if (networks.length > 0) {
        const firstNetwork = networks[0];
        const currencies =
          data.result.capabilities.currenciesByNetworks[firstNetwork];
        if (currencies && currencies.length > 0) {
          const defaultSource = currencies[0];
          return {
            currencySymbol: defaultSource.symbol,
            currency: defaultSource.type,
            network: firstNetwork,
          };
        }
      }
    }
    return null;
  }, [data]);

  const getSourceCurrenciesByNetworks = useCallback((): Record<
    string,
    { symbol: string; type: string }[]
  > | null => {
    if (data && data.result?.capabilities?.currenciesByNetworks) {
      return data.result?.capabilities?.currenciesByNetworks;
    }
    return null;
  }, [data]);

  return {
    data,
    isPending,
    getDefaultSource: _getDefaultSource,
    getSourceCurrenciesByNetworks,
  };
};

export const useDestinationCapabilities = ({
  sourceCurrencySymbol,
  sourceNetwork,
}: {
  sourceCurrencySymbol: string;
  sourceNetwork: string;
}) => {
  const { hasAnyOrganization } = useCurrentUserDetails();
  const { getAccessTokenSilently } = useAuth0();
  const { data, isPending } = useQuery({
    queryKey: [
      'transfer-destination-capabilities',
      sourceCurrencySymbol,
      sourceNetwork,
    ],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/public/asset-transfers/destination-capabilities`,
        {
          params: {
            sourceCurrencySymbol,
            sourceNetwork,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: hasAnyOrganization && !!sourceCurrencySymbol && !!sourceNetwork,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

  const _getDefaultDestination = useCallback((): {
    currencySymbol: string;
    currency: string;
    network: string;
  } | null => {
    if (data && data.result?.capabilities?.destinationCurrenciesByNetworks) {
      const networks = Object.keys(
        data.result.capabilities.destinationCurrenciesByNetworks
      );
      if (networks.length > 0) {
        const firstNetwork = networks[0];
        const currencies =
          data.result.capabilities.destinationCurrenciesByNetworks[
            firstNetwork
          ];
        if (currencies && currencies.length > 0) {
          const defaultDest = currencies[0];
          return {
            currencySymbol: defaultDest.symbol,
            currency: defaultDest.type,
            network: firstNetwork,
          };
        }
      }
    }
    return null;
  }, [data]);

  const getAvailableDestinationCurrenciesByNetworks = useCallback(() => {
    if (data && data.result?.capabilities?.destinationCurrenciesByNetworks) {
      return data.result?.capabilities?.destinationCurrenciesByNetworks;
    }
    return {};
  }, [data]);

  return {
    isPending,
    getDefaultDestination: _getDefaultDestination,
    getAvailableDestinationCurrenciesByNetworks,
  };
};

export const useTransferQuote = ({
  requestedAmountType,
  amount,
  sourceNetwork,
  sourceCurrencySymbol,
  destinationCurrencySymbol,
  destinationNetwork,
}: {
  requestedAmountType: 'source' | 'destination';

  amount: string;
  sourceNetwork: string;
  sourceCurrencySymbol: string;
  destinationCurrencySymbol: string;
  destinationNetwork: string;
}) => {
  const { hasAnyOrganization, getCurrentOrganizationId } =
    useCurrentUserDetails();
  const { getAccessTokenSilently } = useAuth0();

  const { data, isFetching } = useQuery({
    queryKey: [
      'transfer-quote',
      requestedAmountType,
      amount,
      sourceNetwork,
      sourceCurrencySymbol,
      destinationCurrencySymbol,
      destinationNetwork,
    ],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/public/asset-transfers/quote`,
        {
          params: {
            organizationId: getCurrentOrganizationId(),
            requestedAmountType,
            amount,
            transferCandidate: {
              sourceNetwork,
              sourceCurrencySymbol,
              destinationCurrencySymbol,
              destinationNetwork,
            },
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.result;
    },
    enabled:
      hasAnyOrganization &&
      !!sourceCurrencySymbol &&
      !!sourceNetwork &&
      !!destinationCurrencySymbol &&
      !!destinationNetwork &&
      !!requestedAmountType &&
      !!amount,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

  return {
    isFetching,
    data,
  };
};
